import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Space, Tag, Alert, Empty, Spin, Button, Input, InputNumber,
  Tooltip, Modal, Typography, Segmented, message,
} from 'antd';
import { SaveOutlined, HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../libs/api';
import ShGpModel from './ShGpModel';
import PwGpModel from './PwGpModel';
import ScenarioDrawer from './ScenarioDrawer';
import {
  INK, NEUTRAL, UNDER, BUDGET_INK, FAINT, fmtMoney, fmtPct, frameColumns,
} from './shared';

const round0 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * GP Control — the assumptions behind revenue, cost of sales and gross profit.
 *
 * Profit and loss basis throughout. Nothing here is cash: it answers what is
 * earned and what it costs, and when the money moves is the cashflow's
 * question.
 *
 * This page owns the comparison and the save; the model underneath owns the
 * assumptions. That split is the point — the actual columns are Xero's own
 * profit and loss and the forecast columns are the Financial Planning engine,
 * and having one component lay out both is what stops them being drawn to
 * different scales or, worse, one filling a gap in the other.
 *
 * What gets saved is the model's output, not only its inputs. The engine runs
 * against live CRM pipeline, so re-deriving a saved month later would let it
 * change under someone the moment a project reschedules — and the statements,
 * deriving separately, would drift from the page they roll up.
 */

export default function GpControl({ entity }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [model, setModel] = useState(null);      // { byMonth, driversByMonth, assumptions }
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  /**
   * The scenario picker, and the scenario a person chose from it.
   *
   * Held here rather than in either model so both entities get the same drawer,
   * and passed down as a request with a timestamp: the model applies it when
   * the request changes, which lets the same scenario be re-loaded after
   * someone has edited away from it.
   */
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [loadRequest, setLoadRequest] = useState(null);
  /**
   * Manual overrides on a forecast account, by month.
   *
   * The model forecasts residential and PPA. Commercial and VPP it does not
   * forecast at all, so their columns were blank and there was no way to say
   * what they are expected to do. These are that way: a figure typed against
   * one account in one month, which replaces whatever the model would have put
   * there — or supplies the only figure, where the model had none.
   *
   * `null` means "whatever was saved". An object means somebody has edited,
   * which is also what takes the page off the saved budget.
   */
  const [overrideEdits, setOverrideEdits] = useState(null);
  const [editingCell, setEditingCell] = useState(null);   // `${month}|${key}`
  /**
   * Which timing the table is on.
   *
   * `booked` is when the ledger records each half of a job: an upfront sale
   * invoices at closing and its installation cost arrives months later, so the
   * two halves land in different months and the monthly margin swings without
   * the business changing at all.
   *
   * `matched` keeps both halves together by going back to the job. Every
   * project that has had a welcome pack issued is placed in the month it was
   * issued, carrying its own Xero revenue, cost and gross profit — the same
   * figures the Residential page reports per project, summed by month rather
   * than read one at a time.
   *
   * These are Xero's own numbers, not a quoted margin: revenue is the credits
   * on income accounts, cost is everything else that hit the job, and the
   * difference is the gross profit. The welcome pack is the cohort because it
   * is already the point this business treats a job as earned — the same gate
   * Acct Revenue uses on the Residential page.
   *
   * Booked is the default because it is what Xero will report monthly and what
   * the statements have to tie to. Matched is the one that says what each
   * month's work actually earned.
   */
  const [basis, setBasis] = useState('booked');
  const [cohort, setCohort] = useState(null);
  const [cohortError, setCohortError] = useState(null);
  // The cohort read is the eighteen-second retail-finance summary, so the
  // table has to say it is working rather than sitting there with empty
  // actual columns that look like a result.
  const [cohortLoading, setCohortLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    /**
     * Drop the previous entity's data before asking for the next.
     *
     * The model component switches the instant the entity does, but the data
     * did not — so PW's model mounted against ShineHub's saved scenario and
     * read a set of drivers that has no `sales` and no `purchaseB` in it at
     * all. Both boxes came up empty, from a scenario that was never PW's.
     */
    setData(null);
    setOverrideEdits(null);
    setEditingCell(null);
    try {
      const r = await api.get('/api/cashflow/gp', { params: { entity }, timeout: 120000 });
      setData(r.data.data);
      setNote('');
      setOverrideEdits(null);
      setEditingCell(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load GP Control');
      setData(null);
    }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  /**
   * The actual months on a matched basis, cohorted by welcome pack date.
   *
   * Xero's own P&L cannot answer this: it is monthly account totals, and once
   * a job's revenue and cost land in different months there is no way back
   * from a total to the job that produced it. Going project by project is the
   * way round — each one's Xero transactions are already attributed to it by
   * the reference on every line — so the revenue, the cost and the margin can
   * be put back together and counted in one month.
   *
   * Every figure here is Xero's. Revenue is the credits on income accounts
   * plus the STC clearing variation; gross profit is every credit less every
   * debit on the job; cost is the difference between them. Nothing is a quote
   * or an estimate.
   *
   * ShineHub only. PW sells stock rather than projects and has no cohort to
   * match against.
   */
  const loadCohort = useCallback(async () => {
    if (entity !== 'shinehub' || !data) return;
    setCohortError(null);
    setCohortLoading(true);
    try {
      const first = data.actualMonths[0];
      const r = await api.get('/api/retail-finance/summary', {
        params: {
          dateField: 'welcomePackAt',
          start: dayjs(first).format('YYYY-MM-DD'),
          end: dayjs(data.thisMonth).endOf('month').format('YYYY-MM-DD'),
        },
        timeout: 120000,
      });
      const byMonth = {};
      for (const pr of (r.data?.data?.projects || [])) {
        if (!pr.welcomePackAt) continue;
        const m = `${dayjs(pr.welcomePackAt).format('YYYY-MM')}-01`;
        const pm = (pr.paymentMethod || '').toLowerCase();
        const line = /ppa/.test(pm) ? 'ppa' : 'upfront';
        // Xero's figures for this job. acctRevenue is the income credits plus
        // the STC clearing variation; gp is every credit less every debit on
        // the job, with the same variation folded in. Cost is what separates
        // them, so it cannot drift from the other two.
        const revenue = pr.acctRevenue || 0;
        const gp = pr.gp || 0;
        if (!byMonth[m]) byMonth[m] = {};
        if (!byMonth[m][line]) byMonth[m][line] = { revenue: 0, cogs: 0, gp: 0, count: 0, unpriced: 0 };
        const c = byMonth[m][line];
        c.revenue += revenue;
        c.gp += gp;
        c.cogs += revenue - gp;
        c.count += 1;
        // A welcome pack has gone out but Xero holds nothing against the job.
        // Counted rather than dropped: it is a job whose paperwork is ahead of
        // its ledger, and hiding it would make the month look smaller than it
        // is rather than showing that something is missing.
        if (!(revenue || gp)) c.unpriced += 1;
      }
      for (const m of Object.keys(byMonth)) {
        for (const line of Object.keys(byMonth[m])) {
          const c = byMonth[m][line];
          c.revenue = Math.round(c.revenue * 100) / 100;
          c.cogs = Math.round(c.cogs * 100) / 100;
          c.gp = Math.round(c.gp * 100) / 100;
        }
      }
      setCohort(byMonth);
    } catch (err) {
      setCohortError(err.response?.data?.message || 'Could not read welcome-pack projects from the CRM');
      setCohort(null);
    } finally {
      setCohortLoading(false);
    }
  }, [entity, data]);

  useEffect(() => { if (basis === 'matched') loadCohort(); }, [basis, loadCohort]);

  // The model reports upward on every keystroke; this must not re-render it.
  const onForecast = useCallback((next) => setModel(next), []);

  /** The overrides in force: the edits if there are any, else what was saved. */
  const overrides = useMemo(() => {
    if (overrideEdits) return overrideEdits;
    const out = {};
    for (const m of (data?.forecastMonths || [])) {
      const saved = data.saved?.[m]?.outputs?.overrides;
      if (saved && Object.keys(saved).length) out[m] = { ...saved };
    }
    return out;
  }, [overrideEdits, data]);

  /**
   * Whether the figures on screen are still the saved budget.
   *
   * This matters more than it looks. The model re-runs against live CRM
   * pipeline every time the page loads, so running it from the saved drivers
   * does not reproduce the saved numbers — a project that rescheduled moves
   * them. A budget that moves on its own is not a budget, so while nothing has
   * been touched the page shows what was saved, not what the model now makes
   * of the same inputs.
   */
  const hasSaved = !!(data && data.forecastMonths.some((m) => data.saved?.[m]?.outputs));
  const showingSaved = hasSaved && !model?.touched && overrideEdits === null;

  // Nothing to save while the screen is the saved version, by definition.
  const dirty = !showingSaved;

  /**
   * How a business line's forecast is spread across its own accounts.
   *
   * The model forecasts a line, not a chart of accounts, so the split has to
   * come from somewhere: it is each account's share of its line over the three
   * complete months on the left of this table. That is an assumption, and it is
   * labelled as one — but a forecast that only exists as a single total is a
   * forecast nobody can check against the account it will actually land in.
   *
   * Weights are per line and per kind, so a revenue account is never given a
   * share of a cost forecast.
   */
  const allocation = useMemo(() => {
    if (!data) return {};
    const base = (data.actualMonths || []).filter((m) => m < data.thisMonth);
    const byAccount = new Map((data.accounts || []).map((a) => [a.accountCode || a.name, a]));
    const out = {};
    for (const line of (data.businessLines || [])) {
      const weights = { revenue: {}, cogs: {} };
      for (const kind of ['revenue', 'cogs']) {
        const members = line.accounts.filter((a) => a.kind === kind);
        const magnitude = (a) => base.reduce(
          (t, m) => t + Math.abs(byAccount.get(a.code || a.name)?.byMonth?.[m] || 0),
          0,
        );
        const total = members.reduce((t, a) => t + magnitude(a), 0);
        if (!total) {
          // No history to go on. Spread evenly rather than dropping the line's
          // forecast entirely — the total is still right either way.
          for (const a of members) weights[kind][a.code || a.name] = members.length ? 1 / members.length : 0;
          continue;
        }
        for (const a of members) weights[kind][a.code || a.name] = magnitude(a) / total;
      }
      out[line.key] = weights;
    }
    return out;
  }, [data]);

  /**
   * The same weights, ignoring business lines.
   *
   * A model that does not split its forecast by line — PW's does not, it sells
   * stock rather than projects — has one total to spread over every account of
   * a kind. Allocating that total inside each line separately handed the whole
   * entity's revenue to each line in turn, so the four accounts under Revenue
   * added up to twice the row above them.
   */
  const globalAllocation = useMemo(() => {
    if (!data) return {};
    const base = (data.actualMonths || []).filter((m) => m < data.thisMonth);
    const out = { revenue: {}, cogs: {} };
    for (const kind of ['revenue', 'cogs']) {
      const members = (data.accounts || []).filter((a) => a.kind === kind);
      const magnitude = (a) => base.reduce((t, m) => t + Math.abs(a.byMonth?.[m] || 0), 0);
      const total = members.reduce((t, a) => t + magnitude(a), 0);
      for (const a of members) {
        const key = a.accountCode || a.name;
        out[kind][key] = total ? magnitude(a) / total : (members.length ? 1 / members.length : 0);
      }
    }
    return out;
  }, [data]);

  /**
   * The complete actual months, memoised.
   *
   * Built here rather than inline in the JSX because the model derives its
   * assumptions from it: a fresh object every render gave the model a fresh
   * dependency every render, which refired its Xero fetch on a loop.
   */
  const actuals = useMemo(() => {
    if (!data) return { byMonth: {}, months: [] };
    return {
      byMonth: data.actual || {},
      months: (data.actualMonths || []).filter((m) => m < data.thisMonth),
    };
  }, [data]);

  /** Which line an account belongs to, as the server classified it. */
  const lineOfAccount = useMemo(() => {
    const out = {};
    for (const line of (data?.businessLines || [])) {
      for (const a of line.accounts) out[a.code || a.name] = line;
    }
    return out;
  }, [data]);

  const matched = basis === 'matched';

  /**
   * Whether the model behind this entity reports a per-line split at all.
   *
   * ShineHub's does — it forecasts projects, and a project has a payment type.
   * PW's does not; it forecasts one number. Everything that allocates a
   * forecast has to know which, or it will spread one total as if it were
   * several.
   */
  const splitsByLine = (data?.forecastMonths || []).some(
    (m) => (model?.byMonth?.[m] || data?.saved?.[m]?.outputs || {}).byLine,
  );


  const sh = entity === 'shinehub';

  /**
   * One month of one line's forecast.
   *
   * ShineHub's model reports a per-line split; PW's does not, so its whole
   * forecast is treated as one line. Falls back to the last saved version when
   * the model has not reported yet, so the table is never briefly empty on load.
   */
  const lineForecast = (lineKey, m) => {
    const out = model?.byMonth?.[m] || data?.saved?.[m]?.outputs || null;
    if (!out) return null;
    const byLine = basis === 'matched' ? out.matchedByLine : out.byLine;
    if (byLine) return byLine[lineKey] || null;
    const src = basis === 'matched' ? (out.matched || out) : out;
    return { revenue: src.revenue, cogs: src.cogs, gp: src.gp };
  };

  /**
   * Revenue, cost of sales and gross profit, each opening onto what is inside.
   *
   * Revenue and cost open onto the accounts that make them up. Gross profit
   * opens onto the business lines instead, because that is the split the
   * business is actually run on — GTL and residential are different businesses
   * on different margins, and one blended GP% hides whichever of them is in
   * trouble. Each line names the accounts behind it, so a mapping that is wrong
   * is visible rather than quietly producing two wrong margins.
   */
  /**
   * What the model alone would put against one account in one month.
   *
   * The line total is modelled; the split inside a line is that account's share
   * of the three complete months on the left. An account in a line the model
   * does not forecast has no figure at all, which is what makes an override
   * the only way to say anything about it.
   */
  const allocatedFor = (kind, accountKey, m) => {
    const line = lineOfAccount[accountKey];
    const weight = splitsByLine
      ? allocation[line?.key]?.[kind]?.[accountKey]
      : globalAllocation[kind]?.[accountKey];
    if (weight == null) return null;
    if (!splitsByLine) {
      const total = model?.byMonth?.[m] || data?.saved?.[m]?.outputs;
      return total ? (total[kind] || 0) * weight : null;
    }
    if (!line?.modelled) return null;
    const fc = lineForecast(line.key, m);
    return fc ? fc[kind] * weight : null;
  };

  const overrideKey = (kind, accountKey) => `${kind}:${accountKey}`;
  const overrideOf = (kind, accountKey, m) => overrides?.[m]?.[overrideKey(kind, accountKey)];

  /** How far this month's overrides move revenue and cost from the model. */
  const overrideDelta = (m) => {
    const out = { revenue: 0, cogs: 0 };
    for (const [key, value] of Object.entries(overrides?.[m] || {})) {
      const [kind, accountKey] = key.split(/:(.+)/);
      if (out[kind] == null) continue;
      out[kind] += (Number(value) || 0) - (allocatedFor(kind, accountKey, m) || 0);
    }
    return out;
  };

  const setOverride = (kind, accountKey, m, value) => {
    setOverrideEdits((prev) => {
      const base = prev || JSON.parse(JSON.stringify(overrides));
      const month = { ...(base[m] || {}) };
      if (value == null || value === '') delete month[overrideKey(kind, accountKey)];
      else month[overrideKey(kind, accountKey)] = Math.round(Number(value)) || 0;
      const next = { ...base };
      if (Object.keys(month).length) next[m] = month;
      else delete next[m];
      return next;
    });
  };

  const save = async () => {
    if (!model) return;
    setSaving(true);
    try {
      const months = {};
      for (const m of data.forecastMonths) {
        const out = model.byMonth?.[m] || null;
        const manual = overrides?.[m] || null;
        /**
         * The saved totals include the overrides, because that is what the
         * forecast is. The Statements tab reads these figures and knows
         * nothing about the model behind them, so anything typed here has to
         * be inside the number rather than beside it. The map is kept too, so
         * reloading the scenario puts the same cells back in the same places.
         */
        const d = manual ? overrideDelta(m) : { revenue: 0, cogs: 0 };
        /**
         * The account breakdown, saved rather than left to be re-derived.
         *
         * The split across accounts is this page's own assumption — each
         * account's share of the three complete months on the left — and it is
         * computed in the browser from data the Statements tab never sees. So
         * either it is written down here or the account rows there stay empty,
         * which is what was happening: revenue and cost arrived as totals with
         * nothing to expand onto.
         *
         * It is also the same reason the totals are saved rather than modelled
         * again: the weights move as the actual months roll forward, and a
         * budget that reshuffles itself between accounts after it was approved
         * is not a budget.
         */
        const accounts = [];
        for (const kind of ['revenue', 'cogs']) {
          for (const a of (data.accounts || []).filter((x) => x.kind === kind)) {
            const key = a.accountCode || a.name;
            const value = overrideOf(kind, key, m) ?? allocatedFor(kind, key, m);
            if (value == null) continue;
            accounts.push({
              kind,
              accountCode: a.accountCode || null,
              accountName: a.name,
              amount: round0(value),
              // Whether a person typed this figure or the split produced it.
              // The Statements tab shows the two differently.
              manual: overrideOf(kind, key, m) != null,
            });
          }
        }
        months[m] = {
          drivers: model.driversByMonth?.[m] || null,
          assumptions: model.assumptions || null,
          outputs: out ? {
            ...out,
            revenue: round0(out.revenue + d.revenue),
            cogs: round0(out.cogs + d.cogs),
            gp: round0(out.gp + d.revenue - d.cogs),
            accounts,
            overrides: manual && Object.keys(manual).length ? manual : undefined,
          } : null,
        };
      }
      const r = await api.post('/api/cashflow/save', {
        entity, kind: 'gp', months, note: note || null,
      });
      const n = r.data.data.saved.length;
      message.success(`Saved ${n} month${n === 1 ? '' : 's'}`);
      await load();
    } catch (err) {
      message.error(err.response?.data?.message || 'Could not save');
    }
    setSaving(false);
  };

  const openHistory = async (month) => {
    setHistory({ month, loading: true, versions: [] });
    try {
      const r = await api.get('/api/cashflow/history', {
        params: { entity, kind: 'gp', month: month.slice(0, 7) },
      });
      setHistory({ month, loading: false, versions: r.data.data.versions });
    } catch (err) {
      setHistory({
        month,
        loading: false,
        versions: [],
        error: err.response?.data?.message || 'Could not read the history',
      });
    }
  };

  if (error) return <Alert type="error" showIcon message={error} />;
  if (!data) {
    return (
      <Card size="small">
        <Spin spinning={loading}>
          <Empty description={loading ? 'Reading the profit and loss…' : 'No data'} />
        </Spin>
      </Card>
    );
  }

  const { months, thisMonth, forecastMonths } = data;

  const pick = (kind, m) => {
    if (!m) return null;
    if (kind === 'budget') {
      /**
       * While nothing has been touched, a budget column is what was saved —
       * not what the model now makes of the same drivers. The engine re-runs
       * against live CRM pipeline, so a project that rescheduled would move a
       * figure somebody had committed to, and a budget that moves on its own
       * is not a budget.
       */
      if (showingSaved) {
        const s = data?.saved?.[m]?.outputs;
        const src = matched ? (s?.matched || null) : s;
        return src ? { revenue: src.revenue, cogs: src.cogs, grossProfit: src.gp } : null;
      }
      const out = model?.byMonth?.[m] || data?.saved?.[m]?.outputs;
      if (!out) return null;
      const src = matched ? (out.matched || null) : out;
      if (!src) return null;
      // Matched is a restatement of the same jobs on a different timing, and
      // an override is a figure typed against a ledger account — there is no
      // sound way to move one onto the other, so overrides apply as booked.
      if (matched) return { revenue: src.revenue, cogs: src.cogs, grossProfit: src.gp };
      // Each override moves the total by how far it sits from what would
      // otherwise have been shown for that account — so an account the model
      // never forecast adds its whole figure, and one it did adds the
      // difference.
      const d = overrideDelta(m);
      return {
        revenue: src.revenue + d.revenue,
        cogs: src.cogs + d.cogs,
        grossProfit: src.gp + d.revenue - d.cogs,
      };
    }
    if (matched) {
      const lines = cohort?.[m];
      if (!lines) return null;
      const t = Object.values(lines).reduce((acc, c) => ({
        revenue: acc.revenue + c.revenue, cogs: acc.cogs + c.cogs,
      }), { revenue: 0, cogs: 0 });
      return { revenue: t.revenue, cogs: t.cogs, grossProfit: t.revenue - t.cogs };
    }
    const a = data.actual?.[m];
    return a ? { revenue: a.revenue, cogs: a.cogs, grossProfit: a.grossProfit } : null;
  };

  /**
   * Which number a column shows.
   *
   * An `actual` or `mtd` column is Xero. A `budget` column is the model, live —
   * it follows the inputs below as they are typed, so the comparison is against
   * what is on screen rather than against what was last saved.
   */
  const accountChildren = (kind) => (data.accounts || [])
    .filter((a) => a.kind === kind)
    .map((a) => {
      const key = a.accountCode || a.name;
      const line = lineOfAccount[key];
      const weight = splitsByLine
        ? allocation[line?.key]?.[kind]?.[key]
        : globalAllocation[kind]?.[key];
      return {
        key: `${kind}:${key}`,
        label: a.name,
        accountCode: a.accountCode,
        child: true,
        line,
        weight,
        accountKey: key,
        kind,
        editable: true,
        pick: (m, colKind) => {
          if (colKind !== 'budget') return a.byMonth?.[m] ?? null;
          const manual = overrideOf(kind, key, m);
          if (manual != null) return manual;
          return allocatedFor(kind, key, m);
        },
      };
    })
    .sort((x, y) => {
      const sum = (r) => months.reduce((t, m) => t + Math.abs(r.pick(m, 'actual') || 0), 0);
      return sum(y) - sum(x);
    });

  /**
   * What Revenue and Cost of sales open onto when the basis is matched.
   *
   * Not accounts. A ledger account total is a month of postings, and once a
   * job's revenue and its cost are booked in different months there is no way
   * back from the total to the job — so there is nothing to re-cut by install
   * cohort. Leaving the rows blank was honest and useless.
   *
   * The dimension the cohort data actually has is the business line, so that is
   * what opens: revenue, cost and the systems behind them, per line, per month.
   */
  const matchedChildren = (kind) => (data.businessLines || [])
    .filter((l) => l.modelled
      || (data.actualMonths || []).some((m) => cohort?.[m]?.[l.key]))
    .map((l) => ({
      key: `matched:${kind}:${l.key}`,
      label: l.label,
      child: true,
      matchedLine: l,
      // How many welcome packs this line sent in a given month. Shown under
      // the figure it produced, so the reader can see whether a month moved
      // because of volume or because of margin.
      countFor: (m) => cohort?.[m]?.[l.key]?.count || 0,
      pick: (m, colKind) => {
        if (colKind === 'budget') {
          if (!l.modelled) return null;
          const fc = lineForecast(l.key, m);
          return fc ? fc[kind] : null;
        }
        return cohort?.[m]?.[l.key]?.[kind] ?? null;
      },
    }));

  const lineChildren = (data.businessLines || []).map((l) => ({
    key: `line:${l.key}`,
    label: l.label,
    child: true,
    help: l.help,
    accountRule: l.accountRule,
    accounts: l.accounts,
    modelled: l.modelled,
    pick: (m, colKind) => {
      if (colKind === 'budget') {
        // Only residential and PPA are modelled. Commercial and VPP have no
        // forecast at all, which is a different thing from a forecast of nil —
        // and a model with no per-line split has none for any of them.
        if (!splitsByLine || !l.modelled) return null;
        const fc = model?.byMonth?.[m]?.byLine || data.saved?.[m]?.outputs?.byLine;
        return fc?.[l.key]?.gp ?? null;
      }
      if (matched) return cohort?.[m]?.[l.key]?.gp ?? null;
      return l.byMonth?.[m]?.gp ?? null;
    },
  }));

  const withKids = (row, kids) => (kids.length ? { ...row, children: kids } : row);

  const breakdownRows = [
    withKids({
      key: 'revenue',
      label: 'Revenue',
      pick: (m, kind) => pick(kind, m)?.revenue ?? null,
    }, matched ? matchedChildren('revenue') : accountChildren('revenue')),
    withKids({
      key: 'cogs',
      label: 'Cost of sales',
      pick: (m, kind) => pick(kind, m)?.cogs ?? null,
    }, matched ? matchedChildren('cogs') : accountChildren('cogs')),
    withKids({
      key: 'gp',
      label: 'Gross profit',
      strong: true,
      pick: (m, kind) => pick(kind, m)?.grossProfit ?? null,
    }, lineChildren),
  ];

  const breakdownColumns = [
    {
      title: 'Account', dataIndex: 'label', width: 300, fixed: 'left',
      render: (v, r) => {
        if (!r.child) {
          return <span style={{ color: INK, fontWeight: r.strong ? 600 : 500 }}>{v}</span>;
        }
        if (r.accounts) {
          // A business line has to say what it is made of, or a GP% nobody can
          // take apart is a GP% nobody can check.
          const listed = r.accounts.length
            ? r.accounts.map((a) => `${a.kind === 'revenue' ? '+' : '\u2212'} ${a.name}${a.code ? ` (${a.code})` : ''}`).join('\n')
            : 'No accounts fall into this line';
          return (
            <Tooltip
              title={(
                <span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                  {`${r.help}\n\nRule: ${r.accountRule}\n\n${listed}`}
                </span>
              )}
            >
              <span style={{ fontSize: 12, color: NEUTRAL }}>
                {v}
                <span style={{ color: FAINT, marginLeft: 8 }}>
                  {r.accounts.length} account{r.accounts.length === 1 ? '' : 's'}
                </span>
                {!r.modelled && (
                  <Tag style={{ marginLeft: 8, fontSize: 10, lineHeight: '16px' }}>not forecast</Tag>
                )}
              </span>
            </Tooltip>
          );
        }
        if (r.matchedLine) {
          return (
            <Tooltip
              title={(
                <span style={{ fontSize: 12 }}>
                  {r.matchedLine.help}
                  {r.matchedLine.modelled
                    ? ''
                    : ' — no model, so the forecast columns are empty rather than zero'}
                </span>
              )}
            >
              <span style={{ fontSize: 12, color: NEUTRAL }}>{v}</span>
            </Tooltip>
          );
        }
        return (
          <Tooltip
            title={r.line ? (
              <span style={{ fontSize: 12 }}>
                {r.line.label}
                {r.line.modelled && r.weight != null
                  ? ` — forecast columns take ${Math.round(r.weight * 1000) / 10}% of this line, its share over the three months on the left`
                  : ' — not forecast by the model'}
              </span>
            ) : null}
          >
            <span style={{ fontSize: 12, color: NEUTRAL }}>
              {v}
              {r.accountCode && <span style={{ color: FAINT, marginLeft: 8 }}>{r.accountCode}</span>}
            </span>
          </Tooltip>
        );
      },
    },
    ...frameColumns(months, (row, m, kind) => {
      if (!m) return <span style={{ color: FAINT }}>—</span>;

      /**
       * A forecast cell on an account row can be typed into.
       *
       * The model forecasts a business line; Commercial and VPP it does not
       * forecast at all, so those rows had nothing in them and no way to say
       * what they are expected to do. Clicking a cell is that way. It is only
       * ever offered on the booked basis and on the forward columns — there is
       * nothing to override about a month that has already happened.
       */
      const canEdit = row.editable && kind === 'budget' && !matched;
      const cellId = `${m}|${overrideKey(row.kind, row.accountKey)}`;
      const manual = canEdit ? overrideOf(row.kind, row.accountKey, m) : undefined;

      if (canEdit && editingCell === cellId) {
        return (
          <InputNumber
            size="small"
            autoFocus
            // Whole dollars. The value underneath is an allocation — a line
            // total times a share of three months' history — so it arrives
            // with six decimal places of arithmetic nobody typed and nobody
            // wants to edit around.
            precision={0}
            step={1000}
            defaultValue={Math.round(manual ?? allocatedFor(row.kind, row.accountKey, m) ?? 0)}
            style={{ width: '100%' }}
            onBlur={(e) => {
              setOverride(row.kind, row.accountKey, m, e.target.value);
              setEditingCell(null);
            }}
            onPressEnter={(e) => {
              setOverride(row.kind, row.accountKey, m, e.target.value);
              setEditingCell(null);
            }}
          />
        );
      }

      const v = row.pick(m, kind);
      /**
       * How many welcome packs are behind this figure.
       *
       * Only on the actual columns of a matched line: a forecast column has no
       * cohort behind it, and the month total has no single line to count. A
       * month with the same revenue on half the jobs is a different month, and
       * the figure alone does not say which happened.
       */
      const wpCount = (kind !== 'budget' && row.countFor) ? row.countFor(m) : 0;

      const body = (v == null || !v)
        ? <span style={{ color: FAINT }}>{canEdit ? 'set' : '—'}</span>
        : (
          <span style={{
            fontVariantNumeric: 'tabular-nums',
            color: kind === 'budget' ? BUDGET_INK : (row.strong ? UNDER : INK),
            fontWeight: row.strong ? 600 : 400,
            fontSize: row.child ? 12 : 13,
          }}
          >
            {fmtMoney(v)}
          </span>
        );

      if (wpCount > 0) {
        return (
          <div>
            {body}
            <div style={{ color: FAINT, fontSize: 10, lineHeight: '14px', marginTop: 1 }}>
              {wpCount} WP
            </div>
          </div>
        );
      }

      if (!canEdit) return body;
      return (
        <Tooltip title={manual != null
          ? 'Typed in, replacing the model. Click to change; clear the box to go back to the model.'
          : 'Click to set this account for this month'}
        >
          <span
            role="button"
            tabIndex={0}
            onClick={() => setEditingCell(cellId)}
            onKeyDown={(e) => { if (e.key === 'Enter') setEditingCell(cellId); }}
            style={{
              cursor: 'text',
              display: 'inline-block',
              minWidth: 48,
              borderBottom: manual != null ? `1px solid ${BUDGET_INK}` : '1px dashed #e0e5ec',
            }}
          >
            {body}
          </span>
        </Tooltip>
      );
    }, { budgetLabel: 'forecast' }),
  ];

  const ModelComponent = sh ? ShGpModel : PwGpModel;

  /**
   * The output table, held as a value rather than rendered in place.
   *
   * The page reads drivers → result → assumptions → pipeline, and the middle of
   * that sequence belongs to this page while the rest belongs to the model. The
   * model hands its sections back and this is slotted between them.
   */
  const breakdown = (
          <Card
            size="small"
            title="Revenue, cost of sales and gross profit"
            className="gp-breakdown"
            extra={(
              <Space>
                {/* Which of the two the forecast columns are: the figures as
                    committed, or the model running live. They are
                    indistinguishable otherwise and only one is an agreement. */}
                <Tag color={showingSaved ? 'blue' : 'default'}>
                  {showingSaved
                    ? 'showing the saved budget'
                    : (hasSaved ? 'changed from the saved budget' : 'not saved yet')}
                </Tag>
                {/* Matching a job's cost to its revenue only means something
                    where there are jobs. PW sells stock; the toggle was a
                    question about a distinction its business does not have. */}
                {sh && (
                  <Segmented
                    size="small"
                    value={basis}
                    onChange={setBasis}
                    options={[
                      { label: 'As booked', value: 'booked' },
                      { label: 'Matched to WP', value: 'matched' },
                    ]}
                  />
                )}
                <Tag color="blue">P&amp;L basis — accrual, not cash</Tag>
                {dirty && <Tag color="orange">unsaved</Tag>}
                <Button
                  type="primary" size="small" icon={<SaveOutlined />}
                  loading={saving} disabled={!dirty} onClick={save}
                >
                  save forecast
                </Button>
              </Space>
            )}
          >
            <Spin
              spinning={cohortLoading}
              tip="Reading welcome-pack projects and their Xero figures…"
            >
            <Table
              size="small"
              rowKey="key"
              dataSource={breakdownRows}
              columns={breakdownColumns}
              pagination={false}
              scroll={{ x: 300 + 8 * 118 }}
              expandable={{ defaultExpandedRowKeys: [] }}
            />
            </Spin>
            {matched && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 10 }}
                message={(
                  <span style={{ fontSize: 12 }}>
                    <b>Matched to welcome pack.</b> An upfront sale invoices at closing and its
                    installation cost lands months later, so on the booked basis the two halves of
                    one job fall in different months and the monthly margin swings without the
                    business changing. Here every project that has had a welcome pack issued is
                    counted in the month it was issued, carrying its own revenue, cost and gross
                    profit.
                    {' '}These are Xero&apos;s figures, job by job — revenue is the credits on
                    income accounts plus the STC clearing variation, gross profit is every credit
                    less every debit on the job, and cost is what separates them. Nothing here is
                    a quote. Going project by project is the only way to keep a job&apos;s halves
                    together, because a monthly ledger total cannot be traced back to the job that
                    produced it.
                    {' '}Two consequences: only residential and PPA are covered, since commercial
                    and VPP are not tracked as projects, so these columns will not tie to Xero
                    monthly; and the rows open onto business lines rather than accounts, because a
                    cohort knows which job, not which posting.
                    {(() => {
                      // A welcome pack has gone out and Xero holds nothing
                      // against the job. Worth naming: the month looks smaller
                      // than it is, and the reason is missing postings rather
                      // than a quiet month.
                      const missing = Object.values(cohort || {})
                        .flatMap((byLine) => Object.values(byLine))
                        .reduce((t, c) => t + (c.unpriced || 0), 0);
                      if (!missing) return null;
                      return (
                        <>
                          <br />
                          <b>
                            {missing} project{missing === 1 ? ' has' : 's have'} a welcome pack but
                            nothing in Xero, so {missing === 1 ? 'it adds' : 'they add'} nothing to
                            the months below.
                          </b>
                        </>
                      );
                    })()}
                    {cohortError && <><br /><b>{cohortError}</b></>}
                  </span>
                )}
              />
            )}
            <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 8 }}>
              Actual columns are Xero&apos;s profit and loss: revenue as invoiced, cost as incurred.
              {showingSaved
                ? ' Forecast columns are the saved budget — the figures as committed, not what the'
                  + ' model now makes of the same drivers. Change anything below and they follow'
                  + ' the model again.'
                : ' Forecast columns follow the model below as you type — saving stores those'
                  + ' numbers, so the Statements tab shows exactly what is on this screen.'}
              {' '}Open revenue or cost and click a forecast cell to set one account for one month
              by hand — the only way to forecast Commercial and VPP, which the model does not
              cover.
              <br />
              Open revenue or cost to see the accounts. Open gross profit to see it by business
              line — hover a line for the rule and the accounts behind it. In the forecast, upfront
              and BNPL sales both land on Upfront / BNPL and PPA lands on PPA / GTL.
              <br />
              The model forecasts a business line, not an account, so an account&apos;s forecast is
              its line&apos;s figure split by that account&apos;s share of the three complete months
              on the left. The line totals are modelled; the split between accounts inside a line is
              an assumption.
            </div>
            <div style={{ marginTop: 10 }}>
              <Space wrap size={12}>
                <Input
                  size="small"
                  placeholder="Why this change? (kept with the saved version)"
                  value={note}
                  maxLength={500}
                  onChange={(e) => setNote(e.target.value)}
                  style={{ width: 380 }}
                />
                {forecastMonths.map((m) => {
                  const s = data.saved?.[m];
                  return (
                    <span key={m} style={{ fontSize: 11, color: NEUTRAL }}>
                      <b>{dayjs(m).format('MMM')}</b>{' '}
                      {s ? (
                        <Button
                          type="link" size="small" icon={<HistoryOutlined />}
                          onClick={() => openHistory(m)}
                          style={{ fontSize: 11, padding: 0, height: 'auto' }}
                        >
                          v{s.version} · {dayjs(s.savedAt).format('D MMM')}
                          {s.savedBy ? ` · ${s.savedBy}` : ''}
                        </Button>
                      ) : <span style={{ color: FAINT }}>never saved</span>}
                    </span>
                  );
                })}
              </Space>
            </div>
          </Card>
  );


  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        {data.xeroError && (
          <Alert
            type="warning" showIcon
            message={(
              <span style={{ fontSize: 12 }}>
                The profit and loss is unavailable — {data.xeroError}. The forecast columns are
                unaffected; the actual columns are empty rather than zero.
              </span>
            )}
          />
        )}

        {data.unmodelled?.pct > 0 && (
          <Alert
            type="warning"
            showIcon
            message={(
              <span style={{ fontSize: 12 }}>
                <b>The forecast columns cover less of the business than the actual ones.</b>{' '}
                {data.unmodelled.lines.join(' and ')} made {fmtMoney(data.unmodelled.revenue)} over
                the last three months — {data.unmodelled.pct}% of revenue — and the model does not
                forecast {data.unmodelled.lines.length === 1 ? 'it' : 'them'}. Read the forecast
                columns as residential and PPA only, not as total {data.name}.
              </span>
            )}
          />
        )}

        <Calibration rows={data.calibration} />

        <ModelComponent
          startMonth={thisMonth}
          months={forecastMonths}
          saved={data.saved}
          // Stock on hand and the purchase history behind it. Null for
          // ShineHub, which carries none.
          stock={data.stock}
          // The same figures the table above shows, so the model's assumptions
          // can be measured from them. A second endpoint with its own idea of
          // what cost of sales means would be a second answer, and nobody
          // could tell which one the input came from.
          actuals={actuals}
          onOpenScenarios={() => setScenarioOpen(true)}
          loadRequest={loadRequest}
          onForecast={onForecast}
          renderSections={(sec) => (
            <>
              {sec.alerts}
              {sec.drivers}
              {breakdown}
              {sec.assumptions}
              {sec.pipeline}
            </>
          )}
        />

        {(data.warnings || []).length > 0 && (
          <Alert
            type="warning" showIcon
            message={(
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                {data.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            )}
          />
        )}
      </Space>

      <ScenarioDrawer
        entity={entity}
        open={scenarioOpen}
        onClose={() => setScenarioOpen(false)}
        onLoad={(scenario) => setLoadRequest({ at: Date.now(), scenario })}
      />

      <Modal
        open={!!history}
        onCancel={() => setHistory(null)}
        footer={null}
        width={760}
        title={history ? `Forecast history — ${dayjs(history.month).format('MMMM YYYY')}` : ''}
      >
        {history?.loading && <Spin />}
        {history?.error && <Alert type="error" showIcon message={history.error} />}
        {history && !history.loading && !history.error && (
          <Table
            size="small"
            rowKey="version"
            dataSource={history.versions}
            pagination={false}
            locale={{ emptyText: 'Nothing saved for this month yet' }}
            columns={[
              { title: 'v', dataIndex: 'version', width: 44 },
              {
                title: 'Saved', dataIndex: 'savedAt', width: 160,
                render: (v, r) => (
                  <div>
                    <div>{dayjs(v).format('D MMM YYYY HH:mm')}</div>
                    <div style={{ fontSize: 11, color: NEUTRAL }}>{r.savedBy || '—'}</div>
                  </div>
                ),
              },
              {
                title: 'Forecast', dataIndex: 'outputs', width: 230,
                render: (o) => (o ? (
                  <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                    rev {fmtMoney(o.revenue)} · GP {fmtMoney(o.gp)}
                    {o.revenue ? ` (${fmtPct((o.gp / o.revenue) * 100)})` : ''}
                  </span>
                ) : '—'),
              },
              {
                title: 'Drivers', dataIndex: 'drivers',
                render: (d) => (
                  <Typography.Text style={{ fontSize: 11 }} type="secondary">
                    {d ? Object.entries(d).map(([k, v]) => `${k}=${v}`).join('  ') : '—'}
                  </Typography.Text>
                ),
              },
              { title: 'Note', dataIndex: 'note', width: 130, render: (v) => v || '—' },
            ]}
          />
        )}
      </Modal>
    </Spin>
  );
}

/**
 * How the last few months of assumptions actually turned out.
 *
 * A forecast nobody checks against the result is a wish, not a control. This is
 * the only thing that makes the model accountable: the next set of numbers gets
 * entered by someone who can see how the last set did.
 */
function Calibration({ rows }) {
  if (!rows || !rows.length) return null;
  const tone = (pct) => (pct == null ? NEUTRAL : (Math.abs(pct) <= 10 ? UNDER : '#a8071a'));
  return (
    <Card size="small" styles={{ body: { padding: '10px 14px' } }}>
      <div style={{ fontSize: 12, color: NEUTRAL, marginBottom: 8 }}>
        How the saved forecasts turned out — what was saved against what the P&amp;L went on to say
      </div>
      <Space size={24} wrap>
        {rows.map((r) => (
          <div key={r.month}>
            <div style={{ fontSize: 12, color: INK, fontWeight: 600 }}>{dayjs(r.month).format('MMM YY')}</div>
            <div style={{ fontSize: 11, color: NEUTRAL, fontVariantNumeric: 'tabular-nums' }}>
              revenue {fmtMoney(r.forecastRevenue)} → {fmtMoney(r.actualRevenue)}{' '}
              <span style={{ color: tone(r.revenueVariancePct) }}>
                {r.revenueVariancePct == null ? '' : `(${r.revenueVariancePct > 0 ? '+' : ''}${r.revenueVariancePct}%)`}
              </span>
            </div>
            <div style={{ fontSize: 11, color: NEUTRAL, fontVariantNumeric: 'tabular-nums' }}>
              GP {fmtPct(r.forecastGpPct)} → {fmtPct(r.actualGpPct)}
            </div>
          </div>
        ))}
      </Space>
    </Card>
  );
}
